import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo, IdTokenClaims } from '@azure/msal-browser';
import AppHeader from '../AppHeader';
import { HeaderNavigationItem } from '../AppHeader/types';
import { UserProfile } from '../AppHeader/types';
import style from './style.module.css';

export const mapPathToHeaderCurrentPath = (pathname: string): string => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/' || normalizedPath.startsWith('/schedules/')) {
    return '/schedules';
  }
  return normalizedPath;
};

type HeaderClaims = IdTokenClaims & {
  given_name?: string;
  family_name?: string;
  givenName?: string;
  familyName?: string;
};

export const mapAccountToHeaderUserProfile = (
  account?: AccountInfo,
): UserProfile | undefined => {
  if (!account) {
    return undefined;
  }

  const claims = (account.idTokenClaims || {}) as HeaderClaims;
  const claimFirstName = claims.given_name || claims.givenName;
  const claimLastName = claims.family_name || claims.familyName;

  let firstName = claimFirstName?.trim();
  let lastName = claimLastName?.trim();

  if ((!firstName || !lastName) && account.name) {
    const nameParts = account.name
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (nameParts.length === 1) {
      firstName = firstName || nameParts[0];
    } else if (nameParts.length > 1) {
      firstName = firstName || nameParts.slice(0, -1).join(' ');
      lastName = lastName || nameParts[nameParts.length - 1];
    }
  }

  return {
    id: account.localAccountId,
    firstName,
    lastName,
    email: account.username,
    loadingState: 'success',
  };
};

const Frame = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount() || accounts[0];
  const userProfile = mapAccountToHeaderUserProfile(activeAccount);
  const headerCurrentPath = mapPathToHeaderCurrentPath(location.pathname);

  // Define navigation items for the header
  const navigationItems: HeaderNavigationItem[] = useMemo(
    () => [
      {
        id: 'schedules',
        label: 'Schedules',
        path: '/schedules',
        order: 1,
      },
    ],
    [],
  );

  // Handle navigation when menu item is selected
  const handleMenuItemSelected = (item: HeaderNavigationItem) => {
    navigate(item.path);
  };

  return (
    <div>
      <AppHeader
        appName="UO Reporting Auto Exporter"
        navigationItems={navigationItems}
        currentPath={headerCurrentPath}
        onMenuItemSelected={handleMenuItemSelected}
        userProfile={userProfile}
      />

      <div className={style.content}>{children}</div>
    </div>
  );
};

export default Frame;
